/**
 * CRESiteIQ2 Map Constants
 * 
 * Shared constants for the themed map implementation including zoom levels,
 * default settings, opacity presets, and animation configurations.
 */

// Zoom level configurations for different geographic layers
export const ZOOM_LEVELS = {
    MIN: 3,
    MAX: 18,
    DEFAULT: 4,
    STATES: { min: 3, max: 5, name: 'States', range: '3-5' },
    COUNTIES: { min: 6, max: 7, name: 'Counties', range: '6-7' },
    ZIP_CODES: { min: 8, max: 11, name: 'ZIP Codes', range: '8-11' },
    TRACTS: { min: 12, max: 13, name: 'Tracts', range: '12-13' },
    BLOCK_GROUPS: { min: 14, max: 15, name: 'Block Groups', range: '14-15' },
    BLOCKS: { min: 16, max: 18, name: 'Blocks', range: '16+' }
};

// Array of zoom level configurations for iteration
export const ZOOM_LEVEL_ARRAY = [
    ZOOM_LEVELS.STATES,
    ZOOM_LEVELS.COUNTIES,
    ZOOM_LEVELS.ZIP_CODES,
    ZOOM_LEVELS.TRACTS,
    ZOOM_LEVELS.BLOCK_GROUPS,
    ZOOM_LEVELS.BLOCKS
];

// Google Maps dark mode styles
export const DARK_MAP_STYLES = [
    { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
    {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }]
    },
    {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }]
    },
    {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#263c3f" }]
    },
    {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b9a76" }]
    },
    {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#38414e" }]
    },
    {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#212a37" }]
    },
    {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca5b3" }]
    },
    {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#746855" }]
    },
    {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1f2835" }]
    },
    {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#f3d19c" }]
    },
    {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{ color: "#2f3948" }]
    },
    {
        featureType: "transit.station",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }]
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#17263c" }]
    },
    {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#515c6d" }]
    },
    {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#17263c" }]
    }
];

// Default map settings
export const DEFAULT_MAP_SETTINGS = {
    center: { lat: 39.8283, lng: -98.5795 }, // Geographic center of USA
    zoom: ZOOM_LEVELS.DEFAULT,
    mapTypeId: 'roadmap',
    styles: [], // Will be populated based on theme
    options: {
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        zoomControl: false, // Using custom zoom controls
        gestureHandling: 'greedy',
        minZoom: ZOOM_LEVELS.MIN,
        maxZoom: ZOOM_LEVELS.MAX
    }
};

// Opacity presets for data layers
export const OPACITY_PRESETS = {
    LOW: 0.3,
    MEDIUM: 0.6,
    HIGH: 0.9,
    DEFAULT: 0.7
};

// Opacity preset configurations for UI
export const OPACITY_PRESET_OPTIONS = [
    { label: 'Low', value: OPACITY_PRESETS.LOW },
    { label: 'Medium', value: OPACITY_PRESETS.MEDIUM },
    { label: 'High', value: OPACITY_PRESETS.HIGH }
];

// Animation configurations
export const ANIMATION_CONFIG = {
    // Spring physics for panel animations
    SPRING: {
        damping: 25,
        stiffness: 200
    },

    // Panel slide animation
    PANEL_SLIDE: {
        duration: 300,
        easing: 'ease-out',
        from: { x: -400, opacity: 0 },
        to: { x: 0, opacity: 1 }
    },

    // Legend slide-up animation
    LEGEND_SLIDE: {
        duration: 400,
        easing: 'ease-out',
        from: { y: 100, opacity: 0 },
        to: { y: 0, opacity: 1 }
    },

    // Transition durations (in milliseconds)
    DURATIONS: {
        FAST: 200,
        NORMAL: 300,
        SLOW: 400
    }
};

// Layer configurations
export const LAYER_TYPES = {
    CRIME: 'crime',
    AGS: 'ags'
};

export const LAYER_CONFIGS = {
    [LAYER_TYPES.CRIME]: {
        id: LAYER_TYPES.CRIME,
        name: 'Crime Data',
        description: 'Crime statistics and risk levels',
        icon: 'shield',
        minZoom: 10,
        color: 'green',
        dataType: 'crime'
    },
    [LAYER_TYPES.AGS]: {
        id: LAYER_TYPES.AGS,
        name: 'AGS Data',
        description: 'Demographic and statistical data',
        icon: 'chart',
        minZoom: 8,
        color: 'blue',
        dataType: 'ags'
    }
};

// Comparison mode scopes
export const COMPARISON_SCOPES = {
    STATE: 'state',
    COUNTY: 'county',
    VIEWPORT: 'viewport'
};

// Location-based zoom levels for AI-detected locations
// Derived from ZOOM_LEVELS to maintain consistency
// Uses max values for best visibility at each geographic level
export const LOCATION_ZOOM_LEVELS = {
    state: ZOOM_LEVELS.STATES.max,            // 5
    county: ZOOM_LEVELS.COUNTIES.max,         // 7
    city: ZOOM_LEVELS.TRACTS.min,             // 12 (cities use tract-level detail)
    zip: ZOOM_LEVELS.ZIP_CODES.max,           // 11
    tract: ZOOM_LEVELS.TRACTS.max,            // 13
    block_group: ZOOM_LEVELS.BLOCK_GROUPS.max, // 15
    block: ZOOM_LEVELS.BLOCKS.max,            // 18
    default: 10                                // Reasonable default
};

// Risk level configurations for crime data
export const RISK_LEVELS = {
    VERY_LOW: { label: 'Very Low', value: 1, color: '#10b981' },
    LOW: { label: 'Low', value: 2, color: '#84cc16' },
    MODERATE: { label: 'Moderate', value: 3, color: '#eab308' },
    HIGH: { label: 'High', value: 4, color: '#f97316' },
    VERY_HIGH: { label: 'Very High', value: 5, color: '#ef4444' }
};

// Z-index layers for proper stacking
export const Z_INDEX = {
    MAP: 0,
    ZOOM_CONTROLS: 10,
    CONTROL_PANEL: 10,
    MAP_LEGEND: 10,
    FLOATING_HEADER: 20,
    THEME_TOGGLE: 20,
    MODAL: 1000,
    TOAST: 2000
};

// Responsive breakpoints (in pixels)
export const BREAKPOINTS = {
    MOBILE: 768,
    TABLET: 1024,
    DESKTOP: 1280
};

// Control panel dimensions
export const CONTROL_PANEL_WIDTH = {
    DESKTOP: 384, // 96 * 4px
    TABLET: 320,  // 80 * 4px
    MOBILE: '100%'
};

// Map legend dimensions
export const MAP_LEGEND_MAX_HEIGHT = 384; // 96 * 4px

// Debounce delays (in milliseconds)
export const DEBOUNCE_DELAYS = {
    MAP_UPDATE: 300,
    OPACITY_CHANGE: 150,
    SEARCH: 500,
    RESIZE: 250
};

// API cache TTL (in seconds)
export const CACHE_TTL = {
    CRIME_DATA: 3600,    // 1 hour
    AGS_DATA: 3600,      // 1 hour
    GEOCODING: 86400     // 24 hours
};

// Error messages
export const ERROR_MESSAGES = {
    MAP_INIT_FAILED: 'Failed to initialize map. Please refresh the page.',
    CRIME_DATA_FAILED: 'Unable to load crime data. Please try again.',
    AGS_DATA_FAILED: 'Unable to load AGS data. Please try again.',
    GEOLOCATION_DENIED: 'Location access denied. Please enable location services.',
    GEOLOCATION_UNAVAILABLE: 'Location services are unavailable.',
    GEOLOCATION_TIMEOUT: 'Location request timed out. Please try again.',
    NETWORK_ERROR: 'Network error. Please check your connection.'
};

// Success messages
export const SUCCESS_MESSAGES = {
    LOCATION_FOUND: 'Location found successfully',
    FILTERS_APPLIED: 'Filters applied successfully',
    LAYER_ENABLED: 'Layer enabled',
    LAYER_DISABLED: 'Layer disabled'
};
